import {useState, useEffect} from 'react'
import {getRepoTitle} from '@/components/SelfOnboarding/util/getLabel'
import Button from '@/components/GDS/molecules/Button/Button'
import CardWrapper from '@/components/GDS/molecules/CardWrapper/Card'
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import sharedStyles from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {REPO_WIDGET} from '@/components/SelfOnboarding/util/constants'
import {anyWidgetFieldTouched, hasEmptyRequiredFields} from '@/components/SelfOnboarding/util/widgetErrorUtils'

const initialRepo = {
  repoUrl: '',
  branches: [''],
}

import {useDispatch, useSelector} from 'react-redux'
import {setRepos} from '@/store/onboardingSlices'
import {Feedback, Label} from 'pepsico-ds'

import type {Repo} from '@/store/onboardingSlices'
import type {RootState} from '@/store'
import CustomInput from '@/components/common/CustomInputWrapper'

const RepoWidget = ({onValidationChange}: {onValidationChange?: (hasError: boolean) => void}) => {
  type Suggestion = {id: string; label: string; fullObject: any}
  const repos = useSelector((state: {repo: Repo[]}) =>
    Array.isArray(state.repo) && state.repo.length > 0
      ? state.repo.map((repo: any) => ({
          ...repo,
          branches: Array.isArray(repo.branches) ? repo.branches : [''],
        }))
      : [{...initialRepo}],
  )
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState<{
    [idx: number]: {repoUrl?: boolean; [key: `branch_${number}`]: boolean | undefined}
  }>({})
  const dispatch = useDispatch()
  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')

  const updateRepo = (idx: number, value: string) => {
    const newRepos = repos.map((r: any, i: number) => (i === idx ? {...r, repoUrl: value} : r))
    dispatch(setRepos(newRepos))
  }

  const updateRepoBranch = (repoIdx: number, branchIdx: number, value: string) => {
    const newRepos = repos.map((r: any, i: number) => {
      if (i !== repoIdx) return r
      const newBranches = [...(r.branches || [])]
      newBranches[branchIdx] = value
      return {...r, branches: newBranches}
    })
    dispatch(setRepos(newRepos))
  }

  const removeBranch = (repoIdx: number, branchIdx: number) => {
    const newRepos = repos.map((r: Repo, i: number) => {
      if (i !== repoIdx) return r
      const newBranches = [...(r.branches || [])]
      newBranches.splice(branchIdx, 1)
      return {...r, branches: newBranches}
    })
    dispatch(setRepos(newRepos))
    setTouched(prev => {
      const copy = {...prev}
      if (copy[repoIdx]) {
        // Remove the touched state for this branch
        const newTouched = {...copy[repoIdx]}
        delete newTouched[`branch_${branchIdx}`]
        // Shift touched keys for subsequent branches
        for (let i = branchIdx + 1; i < (repos[repoIdx]?.branches?.length || 0); i++) {
          newTouched[`branch_${i - 1}`] = newTouched[`branch_${i}`]
          delete newTouched[`branch_${i}`]
        }
        copy[repoIdx] = newTouched
      }
      return copy
    })
  }

  const addBranch = (repoIdx: number) => {
    const newRepos = repos.map((r: any, i: number) => {
      if (i !== repoIdx) return r
      return {...r, branches: [...(r.branches || []), '']}
    })
    dispatch(setRepos(newRepos))
  }
  const handleBlur = (idx: number, field: string) => {
    setTouched(prev => ({
      ...prev,
      [idx]: {
        ...(prev[idx] || {}),
        [field]: true,
      },
    }))
  }

  const addRepo = () => {
    const newRepos = [...repos, {...initialRepo}]
    dispatch(setRepos(newRepos))
  }

  const removeRepo = (idx: number) => {
    const newRepos = repos.filter((_, i: number) => i !== idx)
    dispatch(setRepos(newRepos))
    setTouched(prev => {
      const copy = {...prev}
      delete copy[idx]
      return copy
    })
  }
  useEffect(() => {
    const anyTouched = anyWidgetFieldTouched(touched)
    const hasError = anyTouched && hasEmptyRequiredFields(['repoUrl'], repos)
    if (onValidationChange) onValidationChange(hasError)
  }, [repos, touched, onValidationChange])

  const widgetTouched = anyWidgetFieldTouched(touched)

  const isValidUrl = (str: string) => {
    try {
      new URL(str)
      return true
    } catch {
      return false
    }
  }

  return (
    <>
      {repos.map((repo: any, idx: number) => (
        <CardWrapper
          cardHeader={{
            title: `Repo ${idx + 1}`,
            rightAccessory:
              idx > 0
                ? {
                    iconButtons: [
                      {
                        icon: 'delete',
                        onClick: () => removeRepo(idx),
                      },
                    ],
                  }
                : {
                    iconButtons: [
                      {
                        icon: 'delete_outline',
                        onClick: () => {
                          dispatch(setRepos([{repoUrl: '', branches: ['']}]))
                          setTouched({})
                        },
                      },
                    ],
                  },
          }}
          cardBody={{
            type: 'slot',
            slot: <></>,
          }}
          state="non-interactive"
          type="basic"
          key={idx}
        >
          <form className="flex w-full flex-col items-start">
            <div className="flex w-[282px] flex-col items-start gap-4">
              <div className="flex w-full items-start gap-4">
                <div className="flex w-[282px] flex-col items-start">
                  <Label className={sharedStyles.autocompleteLabelFont} label={REPO_WIDGET.label} />
                  <AutoCompleteWrapper
                    icon="keyboard_arrow_down"
                    limitSuggestions={30}
                    placeholderText={REPO_WIDGET.placeholder}
                    onClear={() => updateRepo(idx, '')}
                    suggestions={suggestions}
                    isAutoComplete={true}
                    isLeadingIcon={false}
                    searchTerm={
                      typeof repo.repoUrl === 'string'
                        ? repo.repoUrl
                        : repo.repoUrl && typeof repo.repoUrl === 'object'
                          ? 'description' in repo.repoUrl
                            ? repo.repoUrl.description
                            : 'label' in repo.repoUrl
                              ? repo.repoUrl.label
                              : ''
                          : ''
                    }
                    onUpdate={async (query: string) => {
                      setLoading(true)
                      setError('')
                      try {
                        const {getItemsByTypeBySearchField} = await import('@/services/team/teamService')
                        const results = await getItemsByTypeBySearchField('scm', {
                          search: query,
                          searchField: 'options.url',
                          size: 30,
                        })
                        const mapped = Array.isArray(results)
                          ? results
                              .map((item: any) => ({
                                id: item.id || item._id || item.value || item.label,
                                label: getRepoTitle(item),
                                fullObject: item,
                              }))
                              .filter(option => option.label && typeof option.label === 'string')
                          : []
                        setSuggestions(mapped)
                        setLoading(false)
                      } catch (err) {
                        setLoading(false)
                        setError('Failed to load suggestions')
                      }
                    }}
                    onSelectOption={(option: any) => updateRepo(idx, option)}
                    onBlur={(e: any) => {
                      const value = e.target.value
                      if (isValidUrl(value)) {
                        updateRepo(idx, value)
                      }
                      handleBlur(idx, 'repoUrl')
                    }}
                  />
                  <Feedback
                    variant="info"
                    className={`${sharedStyles.infoLabel} ${sharedStyles.autocompleteLabelFont}`}
                    message={REPO_WIDGET.info}
                  />
                  {widgetTouched && !repo.repoUrl && (
                    <div className="mt-1 text-xs text-red-500">{REPO_WIDGET.error}</div>
                  )}
                </div>

                {
                  <div className="flex w-full items-start gap-4">
                    <div className="flex w-[282px] flex-col items-start" key={0} style={{flex: 1}}>
                      <CustomInput
                        placeholder={REPO_WIDGET.branchPlaceholder}
                        label={REPO_WIDGET.branchLabel}
                        name={`repoBranch_${idx}_${0}`}
                        value={repo.branches[0]}
                        onChange={e => updateRepoBranch(idx, 0, e.target.value)}
                        onBlur={() => handleBlur(idx, `branch_${0}`)}
                        error={widgetTouched && !repo.branches[0] ? REPO_WIDGET.branchError : ''}
                        helperText={REPO_WIDGET.branchHelperText}
                      />
                    </div>
                  </div>
                }
              </div>

              <div className="flex w-full flex-col items-start gap-4">
                {repo.branches &&
                  repo.branches.length > 1 &&
                  repo.branches.slice(1).map((branch: string, branchIdx: number) => (
                    <div className="flex w-full items-center gap-4" key={branchIdx + 2}>
                      <div className="flex w-[254px] max-w-[400px] flex-shrink-0 flex-col items-start gap-2">
                        <CustomInput
                          customContainerClass="flex w-full items-center gap-4"
                          inputClass={sharedStyles.onboardingInputWithDelete}
                          inputRowClass={sharedStyles.onboardingInputWithDeleteRow}
                          placeholder={REPO_WIDGET.branchPlaceholder}
                          label={REPO_WIDGET.branchLabel}
                          name={`repoBranch_${idx}_${branchIdx + 1}`}
                          value={branch}
                          onChange={e => updateRepoBranch(idx, branchIdx + 1, e.target.value)}
                          onBlur={() => handleBlur(idx, `branch_${branchIdx + 1}`)}
                          error={widgetTouched && !branch ? REPO_WIDGET.branchError : ''}
                          helperText={REPO_WIDGET.branchHelperText}
                        />
                      </div>
                      <Button
                        type="button"
                        iconTrailing="delete"
                        size="medium"
                        variant="tertiary"
                        onClick={() => removeBranch(idx, branchIdx + 1)}
                      />
                    </div>
                  ))}
              </div>

              <div className="flex w-[282px] flex-col items-start">
                <div className={sharedStyles.addPipelineButton}>
                  <Button
                    type="button"
                    iconTrailing="add"
                    size="medium"
                    text={REPO_WIDGET.addBranch}
                    variant="tertiary"
                    onClick={() => addBranch(idx)}
                  />
                </div>
                <Label className={sharedStyles.infoLabel} label={REPO_WIDGET.addBranchHelper} />
              </div>
            </div>
          </form>
        </CardWrapper>
      ))}
      <div className={sharedStyles.addPipelineButton}>
        <Button
          type="button"
          iconTrailing="add"
          size="medium"
          style={{marginTop: 8}}
          text={REPO_WIDGET.addRepo}
          variant="tertiary"
          onClick={() => addRepo()}
        />
      </div>
    </>
  )
}

export default RepoWidget
